import React, { useState } from 'react';

// EBP mapping for diagnoses
const EBP_MAPPING = {
  'Generalized Anxiety Disorder': ['Cognitive-Behavioral Therapy (CBT)', 'Acceptance and Commitment Therapy (ACT)', 'Mindfulness-Based Stress Reduction (MBSR)'],
  'Social Anxiety Disorder': ['Cognitive-Behavioral Therapy (CBT)', 'Exposure Therapy', 'Social Skills Training'],
  'Panic Disorder': ['Cognitive-Behavioral Therapy (CBT)', 'Panic Control Treatment', 'Applied Relaxation'],
  'Anxiety': ['Cognitive-Behavioral Therapy (CBT)', 'Acceptance and Commitment Therapy (ACT)', 'Mindfulness-Based Stress Reduction (MBSR)'],
  'GAD': ['Cognitive-Behavioral Therapy (CBT)', 'Acceptance and Commitment Therapy (ACT)', 'Applied Relaxation'],
  'Major Depressive Disorder': ['Cognitive-Behavioral Therapy (CBT)', 'Behavioral Activation', 'Interpersonal Therapy (IPT)'],
  'Depression': ['Cognitive-Behavioral Therapy (CBT)', 'Behavioral Activation', 'Interpersonal Therapy (IPT)'],
  'MDD': ['Cognitive-Behavioral Therapy (CBT)', 'Behavioral Activation', 'Interpersonal Therapy (IPT)'],
  'Persistent Depressive Disorder': ['Cognitive-Behavioral Analysis System of Psychotherapy (CBASP)', 'Behavioral Activation', 'Mindfulness-Based Cognitive Therapy (MBCT)'],
  'Bipolar Disorder': ['Interpersonal and Social Rhythm Therapy (IPSRT)', 'Family-Focused Therapy (FFT)', 'Cognitive-Behavioral Therapy for Bipolar Disorder'],
  'Bipolar I Disorder': ['Interpersonal and Social Rhythm Therapy (IPSRT)', 'Family-Focused Therapy (FFT)', 'Cognitive-Behavioral Therapy for Bipolar Disorder'],
  'Bipolar II Disorder': ['Interpersonal and Social Rhythm Therapy (IPSRT)', 'Family-Focused Therapy (FFT)', 'Cognitive-Behavioral Therapy for Bipolar Disorder'],
  'Post-Traumatic Stress Disorder': ['Prolonged Exposure Therapy', 'Cognitive Processing Therapy (CPT)', 'Eye Movement Desensitization and Reprocessing (EMDR)'],
  'PTSD': ['Prolonged Exposure Therapy', 'Cognitive Processing Therapy (CPT)', 'Eye Movement Desensitization and Reprocessing (EMDR)'],
  'Adjustment Disorder': ['Problem-Solving Therapy', 'Brief CBT', 'Mindfulness-Based Cognitive Therapy (MBCT)'],
  'Attention-Deficit/Hyperactivity Disorder': ['Cognitive-Behavioral Therapy for ADHD', 'Behavioral Parent Training', 'Organization Skills Training'],
  'ADHD': ['Cognitive-Behavioral Therapy for ADHD', 'Behavioral Management', 'Organization Skills Training'],
  'Autism Spectrum Disorder': ['Applied Behavior Analysis (ABA)', 'Social Skills Training', 'Cognitive-Behavioral Therapy (CBT) with autism adaptations'],
  'ASD': ['Applied Behavior Analysis (ABA)', 'Social Skills Training', 'Cognitive-Behavioral Therapy (CBT) with autism adaptations'],
  'Obsessive-Compulsive Disorder': ['Exposure and Response Prevention (ERP)', 'Acceptance and Commitment Therapy (ACT)', 'Metacognitive Therapy'],
  'OCD': ['Exposure and Response Prevention (ERP)', 'Acceptance and Commitment Therapy (ACT)', 'Metacognitive Therapy'],
  'Alcohol Use Disorder': ['Motivational Enhancement Therapy', 'Cognitive-Behavioral Therapy (CBT)', 'Contingency Management'],
  'Substance Use Disorder': ['Motivational Interviewing', 'Community Reinforcement Approach', 'Cognitive-Behavioral Therapy (CBT)'],
  'Borderline Personality Disorder': ['Dialectical Behavior Therapy (DBT)', 'Mentalization-Based Treatment (MBT)', 'Schema Therapy'],
  'BPD': ['Dialectical Behavior Therapy (DBT)', 'Mentalization-Based Treatment (MBT)', 'Schema Therapy'],
  'Schizophrenia': ['Cognitive Behavioral Therapy for Psychosis (CBTp)', 'Social Skills Training', 'Cognitive Remediation'],
  'Schizoaffective Disorder': ['Cognitive Behavioral Therapy for Psychosis (CBTp)', 'Family Psychoeducation', 'Social Skills Training'],
  'default': ['Cognitive-Behavioral Therapy (CBT)', 'Motivational Interviewing', 'Evidence-Based Integrated Approaches']
};

// Get EBPs for a diagnosis
const getEBPsForDiagnosis = (diagnosis) => {
  if (EBP_MAPPING[diagnosis]) return EBP_MAPPING[diagnosis];

  // Check for partial matches
  for (const key of Object.keys(EBP_MAPPING)) {
    if (key.toLowerCase().includes(diagnosis.toLowerCase()) || diagnosis.toLowerCase().includes(key.toLowerCase())) {
      return EBP_MAPPING[key];
    }
  }
  return EBP_MAPPING['default'];
};

// Get assessment tools for a diagnosis
const getAssessmentTools = (diagnosis) => {
  const lowerDiag = diagnosis.toLowerCase();
  if (lowerDiag.includes('depress') || lowerDiag.includes('mdd')) {
    return ['Patient Health Questionnaire (PHQ-9)', 'Beck Depression Inventory-II (BDI-II)', 'Hamilton Depression Rating Scale (HDRS)'];
  } else if (lowerDiag.includes('anxiety') || lowerDiag.includes('gad') || lowerDiag.includes('panic')) {
    return ['Generalized Anxiety Disorder Scale (GAD-7)', 'Beck Anxiety Inventory (BAI)', 'Penn State Worry Questionnaire'];
  } else if (lowerDiag.includes('bipolar')) {
    return ['Mood Disorder Questionnaire (MDQ)', 'Young Mania Rating Scale (YMRS)', 'Internal State Scale (ISS)'];
  } else if (lowerDiag.includes('adhd') || lowerDiag.includes('attention')) {
    return ['Adult ADHD Self-Report Scale (ASRS)', 'Conners Adult ADHD Rating Scales', 'Brown Attention-Deficit Disorder Scales'];
  } else if (lowerDiag.includes('ptsd') || lowerDiag.includes('trauma')) {
    return ['PTSD Checklist for DSM-5 (PCL-5)', 'Impact of Event Scale-Revised (IES-R)', 'Trauma Symptom Inventory (TSI)'];
  }
  return ['Outcome Rating Scale (ORS)', 'Session Rating Scale (SRS)', 'Clinical Outcomes in Routine Evaluation (CORE)'];
};

const CaseloadDiagnosisAnalytics = ({ chartData }) => {
  const [activeTab, setActiveTab] = useState('current');

  if (!chartData) return null;

  const { diagnosisData, uniqueClients, totalDiagnoses, top3, top3Percentage, diversityIndex, practiceType, isSpecialized, primaryDiagnosis } = chartData;

  // Diversity description
  let diversityDescription;
  if (diversityIndex >= 8.5) {
    diversityDescription = 'High diversity - treats wide range of conditions with even distribution';
  } else if (diversityIndex >= 6.0) {
    diversityDescription = 'Moderate diversity - balanced clinical focus with some concentration';
  } else if (diversityIndex >= 3.5) {
    diversityDescription = 'Lower diversity - concentrated focus on specific conditions';
  } else {
    diversityDescription = 'Very low diversity - highly specialized in few conditions';
  }

  // Practice strengths
  let strengths;
  if (diversityIndex >= 8.5) {
    strengths = [
      'Excellent diagnostic diversity with well-distributed caseload',
      'Comprehensive expertise across multiple clinical areas',
      'Strong flexibility to adapt to changing community needs'
    ];
  } else if (diversityIndex >= 6.0) {
    strengths = [
      'Good diagnostic variety with moderate clinical breadth',
      'Balanced approach between specialization and diversity',
      'Ability to serve diverse client needs effectively'
    ];
  } else if (diversityIndex >= 3.5) {
    strengths = [
      'Focused expertise in core diagnostic areas',
      'Developing specialization with some clinical breadth',
      'Clear clinical identity emerging in key areas'
    ];
  } else {
    strengths = [
      'Highly specialized expertise in narrow clinical focus',
      'Deep knowledge and protocols for specific conditions',
      'Strong referral potential from specialized reputation'
    ];
  }

  const tabs = [
    { id: 'current', label: 'Current Analysis', icon: '📊' },
    { id: 'clinical', label: 'Clinical Strategies', icon: '🎯' },
    { id: 'business', label: 'Business Growth', icon: '📋' },
    { id: 'resources', label: 'Evidence-Based Resources', icon: '📚' }
  ];

  return (
    <div className="bg-white rounded-xl shadow-md">
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-4 text-sm font-semibold transition-colors ${
              activeTab === tab.id
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {/* Current Analysis Tab */}
        {activeTab === 'current' && (
          <div>
            <h3 className="text-xl font-bold text-blue-900 mb-6 text-center">Practice Specialization Analysis</h3>

            {/* Metrics Grid */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4 text-center border">
                <p className="text-sm text-gray-600 font-semibold">Total Clients</p>
                <p className="text-2xl font-bold text-blue-600">{uniqueClients}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center border">
                <p className="text-sm text-gray-600 font-semibold">Diagnosis Types</p>
                <p className="text-2xl font-bold text-blue-600">{totalDiagnoses}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center border">
                <p className="text-sm text-gray-600 font-semibold">Diversity Index</p>
                <p className="text-2xl font-bold text-blue-600">{diversityIndex}/10</p>
                <p className="text-xs text-gray-500">Higher = More Diverse</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center border">
                <p className="text-sm text-gray-600 font-semibold">Primary Focus</p>
                <p className="text-2xl font-bold text-blue-600">{primaryDiagnosis?.percentage.toFixed(1)}%</p>
                <p className="text-xs text-gray-500 truncate">{primaryDiagnosis?.diagnosis}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Top Diagnostic Areas */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-bold text-blue-900 mb-3">Top Diagnostic Areas</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Diagnosis</th>
                      <th className="text-right py-2">Count</th>
                      <th className="text-right py-2">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnosisData.slice(0, 5).map((d, i) => (
                      <tr key={i} className="border-b border-gray-200">
                        <td className="py-2 font-medium">{d.diagnosis}</td>
                        <td className="text-right py-2">{d.count}</td>
                        <td className="text-right py-2">{d.percentage.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <h4 className="text-lg font-bold text-blue-900 mt-6 mb-3">Practice Profile</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><span className="font-semibold">Practice type:</span> {practiceType.charAt(0).toUpperCase() + practiceType.slice(1)} clinical practice</li>
                  <li><span className="font-semibold">Diversity level:</span> {diversityDescription}</li>
                  <li><span className="font-semibold">Top 3 diagnoses:</span> {top3Percentage.toFixed(1)}% of total caseload</li>
                  <li><span className="font-semibold">Clinical identity:</span> {isSpecialized ? 'Specialist' : 'Generalist'} provider</li>
                </ul>
              </div>

              {/* Clinical Insights */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-bold text-blue-900 mb-3">Practice Strengths</h4>
                <ul className="space-y-2 text-gray-700">
                  {strengths.map((s, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Clinical Strategies Tab */}
        {activeTab === 'clinical' && (
          <div>
            <h3 className="text-xl font-bold text-blue-900 mb-6 text-center">Evidence-Based Clinical Strategies</h3>

            {/* EBP recommendations for top diagnoses */}
            {top3.map((d, i) => (
              <div key={i} className="mb-6 bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-bold text-blue-700 mb-3">
                  Clinical Approach for {d.diagnosis} ({d.count} clients)
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-semibold text-blue-600 mb-2">Evidence-Based Treatments:</h5>
                    <ul className="space-y-1 text-gray-700">
                      {getEBPsForDiagnosis(d.diagnosis).map((ebp, j) => (
                        <li key={j} className="flex items-start">
                          <span className="text-blue-500 mr-2">•</span>
                          {ebp}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold text-blue-600 mb-2">Key Assessment Tools:</h5>
                    <ul className="space-y-1 text-gray-700">
                      {getAssessmentTools(d.diagnosis).map((tool, j) => (
                        <li key={j} className="flex items-start">
                          <span className="text-blue-500 mr-2">•</span>
                          {tool}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <h5 className="font-semibold text-blue-600 mt-4 mb-2">Treatment Planning Focus:</h5>
                <ul className="space-y-1 text-gray-700">
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Develop standardized protocols for this diagnosis</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Implement measurement-based care for outcome tracking</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Consider group therapy options if you have sufficient volume</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Create specialized intake procedures for efficient assessment</li>
                </ul>
              </div>
            ))}

            {/* Clinical Efficiency Strategies */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-lg font-bold text-blue-700 mb-3">Clinical Efficiency Strategies</h4>
              {diversityIndex >= 6.0 ? (
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Implement transdiagnostic treatment approaches that work across conditions</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Create modular treatment components for flexible application</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Develop comprehensive screening protocols for multiple diagnoses</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Use measurement-based care to track outcomes across conditions</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Consider integrative approaches for comorbid presentations</li>
                </ul>
              ) : (
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Develop standardized assessment and treatment protocols for {primaryDiagnosis?.diagnosis}</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Create structured treatment curricula with prepared materials</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Implement specialized intake procedures for efficient screening</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Consider group therapy options for your most common diagnoses</li>
                  <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Build referral relationships with complementary specialists</li>
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Business Growth Tab */}
        {activeTab === 'business' && (
          <div>
            <h3 className="text-xl font-bold text-blue-900 mb-6 text-center">Business Growth Strategies</h3>

            {/* Marketing Strategies */}
            <div className="mb-6 bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-bold text-blue-700 mb-3">Targeted Marketing Strategies</h4>

              {diversityIndex >= 6.0 ? (
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Comprehensive Provider Marketing:</h5>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <h6 className="font-medium text-gray-800 mb-1">Brand Positioning:</h6>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Position as a comprehensive mental health solution</li>
                        <li>• Emphasize convenience of addressing multiple concerns</li>
                        <li>• Create messaging around holistic and integrated care</li>
                      </ul>
                    </div>
                    <div>
                      <h6 className="font-medium text-gray-800 mb-1">Content Marketing:</h6>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Create content addressing the full range of conditions</li>
                        <li>• Develop materials about how conditions interact</li>
                        <li>• Highlight benefits of integrated treatment approaches</li>
                      </ul>
                    </div>
                    <div>
                      <h6 className="font-medium text-gray-800 mb-1">Referral Targeting:</h6>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Cast wider net across multiple medical specialties</li>
                        <li>• Position as go-to for complex or comorbid cases</li>
                        <li>• Build relationships with general practitioners</li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Specialist Marketing Approach:</h5>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <h6 className="font-medium text-gray-800 mb-1">Brand Positioning:</h6>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Position as local expert in {primaryDiagnosis?.diagnosis}</li>
                        <li>• Develop clear brand identity around your specialty</li>
                        <li>• Create messaging emphasizing specialized expertise</li>
                      </ul>
                    </div>
                    <div>
                      <h6 className="font-medium text-gray-800 mb-1">Content Marketing:</h6>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Create educational content about {primaryDiagnosis?.diagnosis}</li>
                        <li>• Develop blog series addressing common questions</li>
                        <li>• Produce downloadable resources like symptom checklists</li>
                      </ul>
                    </div>
                    <div>
                      <h6 className="font-medium text-gray-800 mb-1">Referral Targeting:</h6>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Target professionals who encounter your specialty</li>
                        <li>• Build relationships with complementary providers</li>
                        <li>• Create professional referral packets</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Revenue Diversification */}
            <div className="mb-6 bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-bold text-blue-700 mb-3">Revenue Diversification Opportunities</h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">
                    {diversityIndex >= 6.0 ? 'Comprehensive Service Expansion:' : 'Specialized Service Expansion:'}
                  </h5>
                  {diversityIndex >= 6.0 ? (
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• <strong>Integrated Care:</strong> Programs addressing comorbid conditions</li>
                      <li>• <strong>Wellness Programs:</strong> Preventative mental health offerings</li>
                      <li>• <strong>Multi-Disciplinary Team:</strong> Add psychiatry, social work</li>
                      <li>• <strong>Comprehensive Testing:</strong> Assessment services across domains</li>
                      <li>• <strong>Corporate Services:</strong> Employee assistance programs</li>
                    </ul>
                  ) : (
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• <strong>Group Therapy:</strong> Specialized groups for {primaryDiagnosis?.diagnosis}</li>
                      <li>• <strong>Intensive Programs:</strong> Day treatment or IOP</li>
                      <li>• <strong>Specialized Assessments:</strong> Comprehensive evaluations</li>
                      <li>• <strong>Family Programs:</strong> Education and support for families</li>
                      <li>• <strong>Training Services:</strong> Consultation to other professionals</li>
                    </ul>
                  )}
                </div>
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Digital Revenue Streams:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <strong>Online Courses:</strong> Self-guided programs based on expertise</li>
                    <li>• <strong>Digital Resources:</strong> Downloadable workbooks and tools</li>
                    <li>• <strong>Professional Training:</strong> Webinars for other clinicians</li>
                    <li>• <strong>Membership Programs:</strong> Ongoing support communities</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Practice Scaling */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-lg font-bold text-blue-700 mb-3">Practice Scaling Strategies</h4>
              <h5 className="font-semibold text-blue-600 mb-2">
                {diversityIndex >= 6.0 ? 'Horizontal Integration (Service Breadth):' : 'Vertical Integration (Specialty Focus):'}
              </h5>
              {diversityIndex >= 6.0 ? (
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Multi-location expansion with similar service mix</li>
                  <li>• Create distinct departments for different clinical areas</li>
                  <li>• Bring in practitioners with complementary specialties</li>
                  <li>• Hub and spoke model with central administration</li>
                </ul>
              ) : (
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Deepen specialization with comprehensive services</li>
                  <li>• Develop training institute in your area of expertise</li>
                  <li>• Build formal network of complementary providers</li>
                  <li>• Hire clinicians with expertise in your focus areas</li>
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Evidence-Based Resources Tab */}
        {activeTab === 'resources' && (
          <div>
            <h3 className="text-xl font-bold text-blue-900 mb-6 text-center">Evidence-Based Practice Resources</h3>

            {/* Core Resources */}
            <div className="mb-6 bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-bold text-blue-700 mb-3">Essential Clinical Resources</h4>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Evidence-Based Practice Guidelines:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <a href="https://www.apa.org/practice/guidelines" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">APA Clinical Practice Guidelines</a></li>
                    <li>• <a href="https://div12.org/treatments/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">APA Division 12 Evidence-Based Treatments</a></li>
                    <li>• <a href="https://www.samhsa.gov/resource-search/ebp" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">SAMHSA Evidence-Based Practices</a></li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Assessment and Monitoring:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <a href="https://www.myoutcomes.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">MyOutcomes - Measurement-Based Care</a></li>
                    <li>• <a href="https://www.psychologytools.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Psychology Tools - Assessment Resources</a></li>
                    <li>• <a href="https://www.therapistaid.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Therapist Aid - Clinical Worksheets</a></li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Professional Development:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Continuing education through professional organizations</li>
                    <li>• Evidence-based treatment manual training programs</li>
                    <li>• Specialized certification courses</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Training Opportunities */}
            <div className="mb-6 bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-bold text-blue-700 mb-3">Specialized Training Opportunities</h4>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">CBT Certification:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <a href="https://beckinstitute.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Beck Institute CBT Training</a></li>
                    <li>• <a href="https://www.academyofct.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Academy of Cognitive Therapy</a></li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Specialized Approaches:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <a href="https://behavioraltech.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">DBT Training (Behavioral Tech)</a></li>
                    <li>• <a href="https://www.emdria.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">EMDR Training and Certification</a></li>
                    <li>• <a href="https://contextualscience.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">ACT Training Resources</a></li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Trauma-Informed Care:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <a href="https://www.istss.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">International Society for Traumatic Stress Studies</a></li>
                    <li>• <a href="https://www.nctsn.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">National Child Traumatic Stress Network</a></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Practice Management */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-lg font-bold text-blue-700 mb-3">Practice Management Resources</h4>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Practice Management:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <a href="https://www.apa.org/practice" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">APA Practice Resources</a></li>
                    <li>• <a href="https://www.simplepractice.com/blog/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">SimplePractice Blog - Practice Tips</a></li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Professional Ethics:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <a href="https://www.apa.org/ethics/code" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">APA Ethics Code</a></li>
                    <li>• Legal and regulatory compliance resources</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-blue-600 mb-2">Quality Improvement:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Measurement-Based Care Implementation</li>
                    <li>• Outcome Monitoring Best Practices</li>
                    <li>• Client Feedback Integration Systems</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Client Diagnostics Section */}
      <div className="border-t border-gray-200 p-6">
        <h3 className="text-xl font-bold text-blue-900 mb-4">Client Diagnostics</h3>
        <div className="space-y-4">
          {diagnosisData.map((d, i) => (
            <div key={i} className="border-l-4 rounded-r-lg p-4 bg-gray-50" style={{ borderLeftColor: d.color }}>
              <div className="flex items-center mb-2">
                <h4 className="text-base font-bold text-gray-900 mr-3">{d.diagnosis}</h4>
                <span
                  className="text-xs font-semibold text-white px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: d.color }}
                >
                  {d.clientCount} {d.clientCount === 1 ? 'client' : 'clients'}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {(d.clientList || []).map((clientId, j) => (
                  <span key={j} className="px-3 py-1 bg-white border border-gray-300 rounded-full text-sm text-gray-700 font-medium shadow-sm">
                    {clientId}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CaseloadDiagnosisAnalytics;
